import { buildComponent } from '#components'

const appBodyObservers = new WeakMap<HTMLElement, MutationObserver>()

const selectors = {
  showcaseViewer: '#showcase-viewer',
  showcaseViewerControls: '.showcase-viewer-control',
  showcaseDismiss: 'button.showcase-dismiss'
}

const ariaLabels = {
  prev: 'Previous slide',
  next: 'Next slide',
  dismiss: 'Close modal'
}

const findShowcaseViewerInNode = (node: Node): HTMLElement | null => {
  if (!(node instanceof HTMLElement)) return null
  if (node.id === 'showcase-viewer') return node

  return node.querySelector(selectors.showcaseViewer)
}

const ensureUntypedButtonsDefaultToButton = (root: HTMLElement) => {
  root.querySelectorAll('button').forEach((btn) => {
    if (!btn.hasAttribute('type')) {
      btn.setAttribute('type', 'button')
    }
  })
}

const labelShowcaseDismissButtons = (root: HTMLElement) => {
  root.querySelectorAll(selectors.showcaseDismiss).forEach((btn) => {
    btn.setAttribute('aria-label', ariaLabels.dismiss)
  })
}

const labelShowcaseViewerControls = (root: HTMLElement) => {
  const controls = root.querySelectorAll(selectors.showcaseViewerControls)

  controls.forEach((el) => {
    if (!(el instanceof HTMLElement)) return

    const button = el.querySelector('button')

    if (!button) return

    if (el.classList.contains('prev')) {
      button.setAttribute('aria-label', ariaLabels.prev)
    } else if (el.classList.contains('next')) {
      button.setAttribute('aria-label', ariaLabels.next)
    }
  })
}

const enhanceShowcaseViewerModal = (root: HTMLElement) => {
  ensureUntypedButtonsDefaultToButton(root)
  labelShowcaseDismissButtons(root)
  labelShowcaseViewerControls(root)
}

buildComponent({
  name: 'app-component',
  onConnected: (component) => {
    const observer = new MutationObserver((records) => {
      let showcaseAdded: HTMLElement | null = null

      for (const record of records) {
        for (const node of record.addedNodes) {
          showcaseAdded = findShowcaseViewerInNode(node)

          if (showcaseAdded) break
        }

        if (showcaseAdded) break
      }

      if (showcaseAdded) {
        enhanceShowcaseViewerModal(showcaseAdded)
      }

      const reviewEl = component.querySelector('#stamped-reviews-widget') as HTMLElement
      const reviewLinks = reviewEl && reviewEl.querySelectorAll('a.stamped-reviews-link')

      if (reviewLinks && reviewLinks.length > 0) {
        reviewLinks.forEach((link, index) => {
          if (!link.hasAttribute('aria-label')) {
            const label = link.innerHTML ? link.innerHTML : `review item ${index + 1}`

            link.setAttribute('aria-label', label)
          }
        })
      }
    })

    appBodyObservers.set(component, observer)

    observer.observe(document.body, { childList: true, subtree: true })
  },
  onDisconnected: (component) => {
    appBodyObservers.get(component)?.disconnect()
    appBodyObservers.delete(component)
  }
})

declare global {
  interface HTMLElementTagNameMap {
    'app-component': HTMLElement
  }
}
